import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable } from 'rxjs';

import { NavigationLink } from '@stylehaven/components';

import {
    ApiFragment,
    ConfigService,
    NavigationQuery,
    RecordNavigationService,
    RecordService,
    RecordType,
    ToastHelper,
    VersioningService
} from '@digihaven/shared';

import { EnvironmentConfig } from '@common/entities/common/environment-config.entity';
import {
    defaultNavigationOptions,
    NavigationOptions,
    PageType
} from '@common/entities/navigation/navigation-options.entity';

@Injectable()
export class NavigationService extends RecordNavigationService {
    constructor(
        protected recordService: RecordService,
        protected router: Router,
        protected toastHelper: ToastHelper,
        versioningService: VersioningService,
        private readonly configService: ConfigService<EnvironmentConfig>
    ) {
        super(recordService, versioningService, router, toastHelper);
    }

    getNavigationLink(navigationQuery: NavigationQuery<ApiFragment>): NavigationLink {
        return this.getNavigationLinkInner(navigationQuery) || super.getNavigationLink(navigationQuery);
    }

    private getNavigationLinkInner(
        navigationQuery: NavigationQuery<ApiFragment>,
        pageType?: PageType
    ): NavigationLink | null {
        switch (navigationQuery.record.getRecordType()) {
            case RecordType.Representation:
                // Special case: opens representation in edepot
                return {
                    route: `${this.configService.config.edepotUrl}records/${navigationQuery.record.getRecordId()}`,
                    target: '_blank'
                };
            case RecordType.PackagingUnit:
                return this.createRecordNavigationLink(navigationQuery, [
                    '/packaging-units',
                    navigationQuery.record.getRecordId()
                ]);
            case RecordType.TransportUnit:
                return this.createRecordNavigationLink(navigationQuery, [
                    '/transport-units',
                    navigationQuery.record.getRecordId()
                ]);
            case RecordType.Transport:
                return this.createRecordNavigationLink(navigationQuery, [
                    '/transports',
                    navigationQuery.record.getRecordId(),
                    this.getTransportSubRoute(pageType)
                ]);
            default:
                return null;
        }
    }

    navigate(
        record: ApiFragment,
        navigationOptions: Partial<NavigationOptions> = defaultNavigationOptions
    ): Observable<boolean> {
        return this.navigateTo(record, { ...defaultNavigationOptions, ...navigationOptions });
    }

    navigateTo(
        record: ApiFragment,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        const { pageType, state } = navigationOptions;
        const navLink: NavigationLink | null = this.getNavigationLinkInner(
            {
                record,
                navigationOptions: {
                    state,
                    editMode: false
                }
            },
            pageType
        );
        return this.navigateToLink(navLink);
    }

    private getTransportSubRoute(pageType?: PageType): string {
        switch (pageType) {
            case PageType.CONTENT:
                return 'transport-units';
            case PageType.EDIT:
                return 'edit';
            case PageType.READONLY:
            default:
                return 'readonly';
        }
    }
}
